import { create, StateCreator } from "zustand";
import {
  createJSONStorage,
  devtools,
  persist,
  StateStorage,
} from "zustand/middleware";
import { get, set, del } from "idb-keyval";
import {
  WorkflowOneActionI,
  WorkflowOneI,
  WorkflowImageI,
} from "../../../../../../libs/interfaces";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { removeImageBlob, saveImageBlob } from "../../../../../../libs/helpers";

// Custom storage object
const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    return (await get(name)) || null;
  },
  setItem: async (name: string, value: string): Promise<void> => {
    await set(name, value);
  },
  removeItem: async (name: string): Promise<void> => {
    await del(name);
  },
};

export const createWorkflowSlice: StateCreator<
  WorkflowOneI & WorkflowOneActionI,
  [],
  []
> = (set, get) => ({
  id: null,
  createdByAdmin: null,
  coordinates: "",
  container: null,
  client: null,
  clientId: null,
  observation: null,
  clientIdentification: null,
  labelSerial: null,
  plateVehicle: "",
  driverName: "",
  driverIdentification: "",
  openedBy: "",
  openedWas: "No",
  hourInit: "",
  hourEnd: "",
  startProcess: "",
  endProcess: "",
  address: "",
  city: "",
  typeReview: "",
  storageName: "",
  quantityImages: 8,
  images: [],
  imagesDelete: [],
  typeContainer: null,
  naviera: null,
  size: null,
  companyTransport: null,
  entryPort: null,
  //FOTOS VACIO EXTERNO
  emptyPanoramicPhoto: "",
  emptyPanoramicComment: null,
  emptyPanoramicCoordinates: "",
  emptyStampNavieraPhoto: "",
  emptyStampNavieraComment: null,
  emptyOtherStampPhoto: "",
  emptyOtherStampComment: null,
  emptySatelliteLockStampPhoto: "",
  emptySatelliteLockStampComment: null,
  emptySatelliteLockPhoto: "",
  emptySatelliteLockComment: null,
  emptyAditionalStampPhoto: "",
  emptyAditionalStampComment: null,
  emptySideRightPhoto: "",
  emptySideLeftPhoto: "",
  emptySideUpPhoto: "",
  emptySideDownPhoto: "",
  emptyFrontPhoto: "",
  emptyRearPhoto: "",
  emptyEirPhoto: "",
  emptyPreviousInspectionDocumentPhoto: "",
  emptyPlatePhoto: "",
  emptyDriverIdentificationPhoto: "",
  //FOTOS VACIO INTERNO
  emptyFloorPhoto: "",
  emptyRoofPhoto: "",
  emptyMirrorCoverPhoto: "",
  emptyInternalPhoto1: "",
  emptyInternalPhoto2: "",
  emptyInternalPhoto3: "",
  emptyInternalPhoto4: "",
  emptyInternalPhoto5: "",
  emptyInternalPhoto6: "",
  emptyFloorComment: null,
  emptyRoofComment: null,
  emptyMirrorCoverComment: null,
  emptyInternalComment1: null,
  emptyInternalComment2: null,
  emptyInternalComment3: null,
  emptyInternalComment4: null,
  emptyInternalComment5: null,
  emptyInternalComment6: null,
  emptyInternalVideo: "",
  //FOTOS FULL
  exitOtherStampPhoto: "",
  exitPanoramicPhoto: "",
  exitStampNavieraPhoto: "",
  exitSatelliteLockStampPhoto: "",
  exitEngineryPhoto1: "",
  exitEngineryPhoto2: "",
  exitOtherStampComment: null,
  exitSatelliteLockStampComment: null,
  exitPanoramicComment: null,
  exitStampNavieraComment: null,
  exitEngineryComment1: null,
  exitEngineryComment2: null,
  exitDoorVideo: "",
  exitEngineryVideo: "",
  getImages: () => get().images,
  setCoordinates: (coordinates) => {
    set(() => ({ coordinates }));
  },
  setOpenedBy: (openedBy) => {
    set(() => ({ openedBy }));
  },
  setOpenedWas: (openedWas) => {
    set(() => ({ openedWas }));
  },
  setObservation: (observation) => {
    set(() => ({ observation }));
  },
  setEmptyPanoramicCoordinates: (emptyPanoramicCoordinates) => {
    set(() => ({ emptyPanoramicCoordinates }));
  },
  setTypeContainer: (typeContainer) => {
    set(() => ({ typeContainer }));
  },
  setSize: (size) => {
    set(() => ({ size }));
  },
  setNaviera: (naviera) => {
    set(() => ({ naviera }));
  },
  setCompanyTransport: (companyTransport) => {
    set(() => ({ companyTransport }));
  },
  setCreatedByAdmin: (createdByAdmin) => {
    set(() => ({ createdByAdmin }));
  },
  setEntryPort: (entryPort) => {
    set(() => ({ entryPort }));
  },

  //FOTOS VACIO EXTERNO
  setEmptyPanoramicPhoto: async (file) => {
    const getImageBack = get().emptyPanoramicPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    const coordinates = get().coordinates;
    set(() => ({
      emptyPanoramicPhoto: photoId,
      emptyPanoramicCoordinates: coordinates,
    }));
  },
  setEmptyPanoramicComment: (emptyPanoramicComment) => {
    set(() => ({ emptyPanoramicComment }));
  },
  setEmptyStampNavieraPhoto: async (file) => {
    const getImageBack = get().emptyStampNavieraPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyStampNavieraPhoto: photoId }));
  },
  setEmptyStampNavieraComment: (emptyStampNavieraComment) =>
    set(() => ({ emptyStampNavieraComment })),
  setEmptyOtherStampPhoto: async (file) => {
    const getImageBack = get().emptyOtherStampPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyOtherStampPhoto: photoId }));
  },
  setEmptyOtherStampComment: (emptyOtherStampComment) =>
    set(() => ({ emptyOtherStampComment })),
  setEmptyAditionalStampPhoto: async (file) => {
    const getImageBack = get().emptyAditionalStampPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyAditionalStampPhoto: photoId }));
  },
  setEmptyAditionalStampComment: (emptyAditionalStampComment) =>
    set(() => ({ emptyAditionalStampComment })),
  setEmptySatelliteLockStampPhoto: async (file) => {
    const getImageBack = get().emptySatelliteLockStampPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptySatelliteLockStampPhoto: photoId }));
  },
  setEmptySatelliteLockStampComment: (emptySatelliteLockStampComment) =>
    set(() => ({ emptySatelliteLockStampComment })),
  setEmptySatelliteLockPhoto: async (file) => {
    const getImageBack = get().emptySatelliteLockPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptySatelliteLockPhoto: photoId }));
  },
  setEmptySatelliteLockComment: (emptySatelliteLockComment) =>
    set(() => ({ emptySatelliteLockComment })),
  setEmptySideRightPhoto: async (file) => {
    const getImageBack = get().emptySideRightPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptySideRightPhoto: photoId }));
  },
  setEmptySideLeftPhoto: async (file) => {
    const getImageBack = get().emptySideLeftPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptySideLeftPhoto: photoId }));
  },
  setEmptySideUpPhoto: async (file) => {
    const getImageBack = get().emptySideUpPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptySideUpPhoto: photoId }));
  },
  setEmptySideDownPhoto: async (file) => {
    const getImageBack = get().emptySideDownPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptySideDownPhoto: photoId }));
  },
  setEmptyFrontPhoto: async (file) => {
    const getImageBack = get().emptyFrontPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyFrontPhoto: photoId }));
  },
  setEmptyRearPhoto: async (file) => {
    const getImageBack = get().emptyRearPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyRearPhoto: photoId }));
  },
  setEmptyEirPhoto: async (file) => {
    const getImageBack = get().emptyEirPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyEirPhoto: photoId }));
  },
  setEmptyPreviousInspectionDocumentPhoto: async (file) => {
    const getImageBack = get().emptyPreviousInspectionDocumentPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyPreviousInspectionDocumentPhoto: photoId }));
  },
  setEmptyPlatePhoto: async (file) => {
    const getImageBack = get().emptyPlatePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyPlatePhoto: photoId }));
  },
  setEmptyDriverIdentificationPhoto: async (file) => {
    const getImageBack = get().emptyDriverIdentificationPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyDriverIdentificationPhoto: photoId }));
  },

  //FOTOS VACIO INTERNO
  setEmptyFloorPhoto: async (file) => {
    const getImageBack = get().emptyFloorPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyFloorPhoto: photoId }));
  },
  setEmptyRoofPhoto: async (file) => {
    const getImageBack = get().emptyRoofPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyRoofPhoto: photoId }));
  },
  setEmptyMirrorCoverPhoto: async (file) => {
    const getImageBack = get().emptyMirrorCoverPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyMirrorCoverPhoto: photoId }));
  },
  setEmptyInternalPhoto1: async (file) => {
    const getImageBack = get().emptyInternalPhoto1;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyInternalPhoto1: photoId }));
  },
  setEmptyInternalPhoto2: async (file) => {
    const getImageBack = get().emptyInternalPhoto2;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyInternalPhoto2: photoId }));
  },
  setEmptyInternalPhoto3: async (file) => {
    const getImageBack = get().emptyInternalPhoto3;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyInternalPhoto3: photoId }));
  },
  setEmptyInternalPhoto4: async (file) => {
    const getImageBack = get().emptyInternalPhoto4;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyInternalPhoto4: photoId }));
  },
  setEmptyInternalPhoto5: async (file) => {
    const getImageBack = get().emptyInternalPhoto5;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyInternalPhoto5: photoId }));
  },
  setEmptyInternalPhoto6: async (file) => {
    const getImageBack = get().emptyInternalPhoto6;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyInternalPhoto6: photoId }));
  },
  setEmptyFloorComment: (emptyFloorComment) =>
    set(() => ({ emptyFloorComment })),
  setEmptyRoofComment: (emptyRoofComment) => set(() => ({ emptyRoofComment })),
  setEmptyMirrorCoverComment: (emptyMirrorCoverComment) =>
    set(() => ({ emptyMirrorCoverComment })),
  setEmptyInternalComment1: (emptyInternalComment1) =>
    set(() => ({ emptyInternalComment1 })),
  setEmptyInternalComment2: (emptyInternalComment2) =>
    set(() => ({ emptyInternalComment2 })),
  setEmptyInternalComment3: (emptyInternalComment3) =>
    set(() => ({ emptyInternalComment3 })),
  setEmptyInternalComment4: (emptyInternalComment4) =>
    set(() => ({ emptyInternalComment4 })),
  setEmptyInternalComment5: (emptyInternalComment5) =>
    set(() => ({ emptyInternalComment5 })),
  setEmptyInternalComment6: (emptyInternalComment6) =>
    set(() => ({ emptyInternalComment6 })),
  setEmptyInternalVideo: async (file) => {
    if (!file) {
      set(() => ({ emptyInternalVideo: "" }));
      return;
    }
    set(() => ({ emptyInternalVideo: file as string }));
  },

  //FOTOS FULL
  setExitOtherStampPhoto: async (file) => {
    const getImageBack = get().exitOtherStampPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitOtherStampPhoto: photoId }));
  },
  setExitPanoramicPhoto: async (file) => {
    const getImageBack = get().exitPanoramicPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitPanoramicPhoto: photoId }));
  },
  setExitStampNavieraPhoto: async (file) => {
    const getImageBack = get().exitStampNavieraPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitStampNavieraPhoto: photoId }));
  },
  setExitSatelliteLockStampPhoto: async (file) => {
    const getImageBack = get().exitSatelliteLockStampPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitSatelliteLockStampPhoto: photoId }));
  },
  setExitSatelliteLockStampComment: (exitSatelliteLockStampComment) =>
    set(() => ({ exitSatelliteLockStampComment })),
  setExitEngineryPhoto1: async (file) => {
    const getImageBack = get().exitEngineryPhoto1;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitEngineryPhoto1: photoId }));
  },
  setExitEngineryPhoto2: async (file) => {
    const getImageBack = get().exitEngineryPhoto2;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitEngineryPhoto2: photoId }));
  },
  setExitOtherStampComment: (exitOtherStampComment) =>
    set(() => ({ exitOtherStampComment })),
  setExitPanoramicComment: (exitPanoramicComment) =>
    set(() => ({ exitPanoramicComment })),
  setExitStampNavieraComment: (exitStampNavieraComment) =>
    set(() => ({ exitStampNavieraComment })),
  setExitEngineryComment1: (exitEngineryComment1) =>
    set(() => ({ exitEngineryComment1 })),
  setExitEngineryComment2: (exitEngineryComment2) =>
    set(() => ({ exitEngineryComment2 })),
  setExitDoorVideo: async (file) => {
    if (!file) {
      set(() => ({ exitDoorVideo: "" }));
      return;
    }
    set(() => ({ exitDoorVideo: file as string }));
  },
  setExitEngineryVideo: async (file) => {
    if (!file) {
      set(() => ({ exitEngineryVideo: "" }));
      return;
    }
    set(() => ({ exitEngineryVideo: file as string }));
  },

  setId: (id) => set(() => ({ id })),
  setHourInit: (hourInit) => set(() => ({ hourInit })),
  setHourEnd: (hourEnd) => set(() => ({ hourEnd })),
  setStartProcess: (startProcess) => set(() => ({ startProcess })),
  setEndProcess: (endProcess) => set(() => ({ endProcess })),
  setAddress: (address) => set(() => ({ address })),
  setCity: (city) => set(() => ({ city })),
  setTypeReview: (typeReview) => set(() => ({ typeReview })),
  setStorageName: (storageName) => set(() => ({ storageName })),

  setContainer: (container) => {
    const elContainer = get().container;
    if (elContainer !== "" || elContainer !== null) {
      const now = moment();
      const hourGenerate = now.format("HH:mm:ss");
      set(() => ({ container, hourInit: hourGenerate }));
    } else {
      set(() => ({ container }));
    }
  },
  setClient: (client) => set(() => ({ client })),
  setClientId: (clientId) => set(() => ({ clientId })),
  setClientIdentification: (clientIdentification) =>
    set(() => ({ clientIdentification })),
  setLabelSerial: (labelSerial) => set(() => ({ labelSerial })),
  setPlateVehicle: (plateVehicle) => set(() => ({ plateVehicle })),
  setDriverName: (driverName) => set(() => ({ driverName })),
  setDriverIdentification: (driverIdentification) =>
    set(() => ({ driverIdentification })),
  setImages: (images) => set(() => ({ images })),
  getOneImage: (uuid) => {
    const items_ = get().images;
    return items_.find((el) => el.uuid === uuid)!;
  },
  addImage: (type: any) => {
    set((state) => ({
      images: [
        ...state.images,
        {
          id: null,
          src: "",
          comment: "",
          uuid: uuidv4(),
          model: "wf-one",
          type,
          order: state.images.length + 1,
        },
      ],
    }));
  },
  addImageDelete: (id) => set(() => ({ id: null })),
  removeImage: async (uuid) => {
    const items_ = get().images;
    const itemsDelete_ = get().imagesDelete!;
    const fill = items_.find((el) => el.uuid === uuid);
    const arrnew = items_.filter((el) => el.uuid !== uuid);
    
    if (fill) await removeImageBlob(fill.src);

    if (fill!.id && !itemsDelete_.includes(fill!.id))
      itemsDelete_.push(fill!.id);

    set(() => ({
      images: arrnew,
      imagesDelete: itemsDelete_,
    }));
  },
  updateImage: async (uuid, field, value) => {
    let laPhoto = "";

    if (uuid && field === "src") {
      await removeImageBlob(uuid);
      const photoId = crypto.randomUUID();

      const blob = await (await fetch(value)).blob();
      await saveImageBlob(photoId, blob);

      laPhoto = photoId;
    }

    set((state) => {
      const items_ = state.images;
      const arrnew: WorkflowImageI[] = [];

      for (const x of items_) {
        if (x.uuid === uuid) {
          const obj = {
            ...x,
            [field]: field === "src" ? laPhoto : value,
          };
          arrnew.push(obj);
        } else {
          arrnew.push(x);
        }
      }
      return {
        images: arrnew,
      };
    });
  },
  getOnlyPhotos: () => ({
    emptyAditionalStampPhoto: get().emptyAditionalStampPhoto,
    emptyPanoramicPhoto: get().emptyPanoramicPhoto,
    emptyStampNavieraPhoto: get().emptyStampNavieraPhoto,
    emptyOtherStampPhoto: get().emptyOtherStampPhoto,
    emptySatelliteLockStampPhoto: get().emptySatelliteLockStampPhoto,
    emptySatelliteLockPhoto: get().emptySatelliteLockPhoto,
    emptySideRightPhoto: get().emptySideRightPhoto,
    emptySideLeftPhoto: get().emptySideLeftPhoto,
    emptySideUpPhoto: get().emptySideUpPhoto,
    emptySideDownPhoto: get().emptySideDownPhoto,
    emptyFrontPhoto: get().emptyFrontPhoto,
    emptyRearPhoto: get().emptyRearPhoto,
    emptyEirPhoto: get().emptyEirPhoto,
    emptyPreviousInspectionDocumentPhoto: get().emptyPreviousInspectionDocumentPhoto,
    emptyPlatePhoto: get().emptyPlatePhoto,
    emptyDriverIdentificationPhoto: get().emptyDriverIdentificationPhoto,
    emptyFloorPhoto: get().emptyFloorPhoto,
    emptyRoofPhoto: get().emptyRoofPhoto,
    emptyMirrorCoverPhoto: get().emptyMirrorCoverPhoto,
    emptyInternalPhoto1: get().emptyInternalPhoto1,
    emptyInternalPhoto2: get().emptyInternalPhoto2,
    emptyInternalPhoto3: get().emptyInternalPhoto3,
    emptyInternalPhoto4: get().emptyInternalPhoto4,
    emptyInternalPhoto5: get().emptyInternalPhoto5,
    emptyInternalPhoto6: get().emptyInternalPhoto6,
    exitOtherStampPhoto: get().exitOtherStampPhoto,
    exitPanoramicPhoto: get().exitPanoramicPhoto,
    exitStampNavieraPhoto: get().exitStampNavieraPhoto,
    exitSatelliteLockStampPhoto: get().exitSatelliteLockStampPhoto,
    exitEngineryPhoto1: get().exitEngineryPhoto1,
    exitEngineryPhoto2: get().exitEngineryPhoto2,
  }),
  getValues: () => ({
    coordinates: get().coordinates,
    observation: get().observation,
    container: get().container,
    createdByAdmin: get().createdByAdmin,
    typeContainer: get().typeContainer,
    naviera: get().naviera,
    size: get().size,
    companyTransport: get().companyTransport,
    entryPort: get().entryPort,
    openedBy: get().openedBy,
    openedWas: get().openedWas,
    clientId: get().clientId,
    client: get().client,
    labelSerial: get().labelSerial,
    plateVehicle: get().plateVehicle,
    driverName: get().driverName,
    driverIdentification: get().driverIdentification,
    images: get().images,
    imagesDelete: get().imagesDelete,
    hourInit: get().hourInit,
    hourEnd: get().hourEnd,
    startProcess: get().startProcess,
    endProcess: get().endProcess,
    address: get().address,
    city: get().city,
    typeReview: get().typeReview,
    storageName: get().storageName,
    emptyAditionalStampPhoto: get().emptyAditionalStampPhoto,
    emptyAditionalStampComment: get().emptyAditionalStampComment,
    emptyPanoramicPhoto: get().emptyPanoramicPhoto,
    emptyPanoramicComment: get().emptyPanoramicComment,
    emptyPanoramicCoordinates: get().emptyPanoramicCoordinates,
    emptyStampNavieraPhoto: get().emptyStampNavieraPhoto,
    emptyStampNavieraComment: get().emptyStampNavieraComment,
    emptyOtherStampPhoto: get().emptyOtherStampPhoto,
    emptyOtherStampComment: get().emptyOtherStampComment,
    emptySatelliteLockStampPhoto: get().emptySatelliteLockStampPhoto,
    emptySatelliteLockStampComment: get().emptySatelliteLockStampComment,
    emptySatelliteLockPhoto: get().emptySatelliteLockPhoto,
    emptySatelliteLockComment: get().emptySatelliteLockComment,
    emptySideRightPhoto: get().emptySideRightPhoto,
    emptySideLeftPhoto: get().emptySideLeftPhoto,
    emptySideUpPhoto: get().emptySideUpPhoto,
    emptySideDownPhoto: get().emptySideDownPhoto,
    emptyFrontPhoto: get().emptyFrontPhoto,
    emptyRearPhoto: get().emptyRearPhoto,
    emptyEirPhoto: get().emptyEirPhoto,
    emptyPreviousInspectionDocumentPhoto:
      get().emptyPreviousInspectionDocumentPhoto,
    emptyPlatePhoto: get().emptyPlatePhoto,
    emptyDriverIdentificationPhoto: get().emptyDriverIdentificationPhoto,
    emptyFloorPhoto: get().emptyFloorPhoto,
    emptyRoofPhoto: get().emptyRoofPhoto,
    emptyMirrorCoverPhoto: get().emptyMirrorCoverPhoto,
    emptyInternalPhoto1: get().emptyInternalPhoto1,
    emptyInternalPhoto2: get().emptyInternalPhoto2,
    emptyInternalPhoto3: get().emptyInternalPhoto3,
    emptyInternalPhoto4: get().emptyInternalPhoto4,
    emptyInternalPhoto5: get().emptyInternalPhoto5,
    emptyInternalPhoto6: get().emptyInternalPhoto6,
    emptyFloorComment: get().emptyFloorComment,
    emptyRoofComment: get().emptyRoofComment,
    emptyMirrorCoverComment: get().emptyMirrorCoverComment,
    emptyInternalComment1: get().emptyInternalComment1,
    emptyInternalComment2: get().emptyInternalComment2,
    emptyInternalComment3: get().emptyInternalComment3,
    emptyInternalComment4: get().emptyInternalComment4,
    emptyInternalComment5: get().emptyInternalComment5,
    emptyInternalComment6: get().emptyInternalComment6,
    emptyInternalVideo: get().emptyInternalVideo,
    exitOtherStampPhoto: get().exitOtherStampPhoto,
    exitPanoramicPhoto: get().exitPanoramicPhoto,
    exitStampNavieraPhoto: get().exitStampNavieraPhoto,
    exitSatelliteLockStampPhoto: get().exitSatelliteLockStampPhoto,
    exitEngineryPhoto1: get().exitEngineryPhoto1,
    exitEngineryPhoto2: get().exitEngineryPhoto2,
    exitOtherStampComment: get().exitOtherStampComment,
    exitSatelliteLockStampComment: get().exitSatelliteLockStampComment,
    exitPanoramicComment: get().exitPanoramicComment,
    exitStampNavieraComment: get().exitStampNavieraComment,
    exitEngineryComment1: get().exitEngineryComment1,
    exitEngineryComment2: get().exitEngineryComment2,
    exitDoorVideo: get().exitDoorVideo,
    exitEngineryVideo: get().exitEngineryVideo,
  }),
  setValues: (ladata) => {
    set(ladata);
    set(() => ({ imagesDelete: [] }));
  },
  onClear: async () => {
    const state = get();
    const keys = [
      state.emptyAditionalStampPhoto,
      state.emptyPanoramicPhoto,
      state.emptyStampNavieraPhoto,
      state.emptyOtherStampPhoto,
      state.emptySatelliteLockStampPhoto,
      state.emptySatelliteLockPhoto,
      state.emptySideRightPhoto,
      state.emptySideLeftPhoto,
      state.emptySideUpPhoto,
      state.emptySideDownPhoto,
      state.emptyFrontPhoto,
      state.emptyRearPhoto,
      state.emptyEirPhoto,
      state.emptyPreviousInspectionDocumentPhoto,
      state.emptyPlatePhoto,
      state.emptyDriverIdentificationPhoto,
      state.emptyFloorPhoto,
      state.emptyRoofPhoto,
      state.emptyMirrorCoverPhoto,
      state.emptyInternalPhoto1,
      state.emptyInternalPhoto2,
      state.emptyInternalPhoto3,
      state.emptyInternalPhoto4,
      state.emptyInternalPhoto5,
      state.emptyInternalPhoto6,
      state.emptyInternalVideo,
      state.exitOtherStampPhoto,
      state.exitPanoramicPhoto,
      state.exitStampNavieraPhoto,
      state.exitSatelliteLockStampPhoto,
      state.exitEngineryPhoto1,
      state.exitEngineryPhoto2,
      state.exitDoorVideo,
      state.exitEngineryVideo,
    ];

    for (const key of keys) {
      if (
        key &&
        !key.startsWith("http://") &&
        !key.startsWith("https://") &&
        !key.startsWith("data:")
      ) {
        await removeImageBlob(key);
      }
    }

    const images_ = get().images;
    for (const x of images_) {
      if (
        x.src.startsWith("http://") ||
        x.src.startsWith("https://") ||
        x.src.startsWith("data:")
      )
        continue;
      await removeImageBlob(x.src);
    }

    set(() => ({
      id: null,
      container: null,
      emptyPanoramicCoordinates: "",
      typeContainer: null,
      naviera: null,
      size: null,
      companyTransport: null,
      entryPort: null,
      createdByAdmin: null,
      client: null,
      clientId: null,
      clientIdentification: null,
      labelSerial: null,
      plateVehicle: "",
      driverName: "",
      driverIdentification: "",
      openedBy: "",
      openedWas: "No",
      hourInit: "",
      hourEnd: "",
      startProcess: "",
      endProcess: "",
      address: "",
      city: "",
      typeReview: "",
      storageName: "",
      images: [],
      imagesDelete: [],
      step: "Formulario",
      observation: null,
      emptyAditionalStampPhoto: "",
      emptyAditionalStampComment: null,
      emptyPanoramicPhoto: "",
      emptyPanoramicComment: null,
      emptyStampNavieraPhoto: "",
      emptyStampNavieraComment: null,
      emptyOtherStampPhoto: "",
      emptyOtherStampComment: null,
      emptySatelliteLockStampPhoto: "",
      emptySatelliteLockStampComment: null,
      emptySatelliteLockPhoto: "",
      emptySatelliteLockComment: null,
      emptySideRightPhoto: "",
      emptySideLeftPhoto: "",
      emptySideUpPhoto: "",
      emptySideDownPhoto: "",
      emptyFrontPhoto: "",
      emptyRearPhoto: "",
      emptyEirPhoto: "",
      emptyPreviousInspectionDocumentPhoto: "",
      emptyPlatePhoto: "",
      emptyDriverIdentificationPhoto: "",
      emptyFloorPhoto: "",
      emptyRoofPhoto: "",
      emptyMirrorCoverPhoto: "",
      emptyInternalPhoto1: "",
      emptyInternalPhoto2: "",
      emptyInternalPhoto3: "",
      emptyInternalPhoto4: "",
      emptyInternalPhoto5: "",
      emptyInternalPhoto6: "",
      emptyFloorComment: null,
      emptyRoofComment: null,
      emptyMirrorCoverComment: null,
      emptyInternalComment1: null,
      emptyInternalComment2: null,
      emptyInternalComment3: null,
      emptyInternalComment4: null,
      emptyInternalComment5: null,
      emptyInternalComment6: null,
      emptyInternalVideo: "",
      exitOtherStampPhoto: "",
      exitPanoramicPhoto: "",
      exitStampNavieraPhoto: "",
      exitSatelliteLockStampPhoto: "",
      exitEngineryPhoto1: "",
      exitEngineryPhoto2: "",
      exitOtherStampComment: null,
      exitSatelliteLockStampComment: null,
      exitPanoramicComment: null,
      exitStampNavieraComment: null,
      exitEngineryComment1: null,
      exitEngineryComment2: null,
      exitDoorVideo: "",
      exitEngineryVideo: "",
    }));
  },
  step: "Formulario",
  setStep: (step) => set(() => ({ step })),
  resetSteps: () =>
    set(() => ({
      step: "Formulario",
    })),
});

export const useWorkflowOneStore = create<WorkflowOneI & WorkflowOneActionI>()(
  devtools(
    persist(
      (...a) => ({
        ...createWorkflowSlice(...a),
      }),
      {
        name: "workflow-one-ecupro",
        storage: createJSONStorage(() => storage),
      }
    )
  )
);